import React, { useEffect, useState } from "react";
import { DateTime } from "luxon";
import profileStyles from "../CustomerProfile.module.scss";
import { APIService } from "../../../shared/shared-with-mobile/api-client/api.service";
import {
  HubspotCustomerSummary,
  HubspotDeal,
  HubspotEngagement,
} from "../../../shared/shared-with-mobile/api-client/services/admin-analytics.service";

// ============================================================
// Customer Profile → HubSpot tab
//
// Pulls imported HubSpot data linked to this license-holder via the
// external_link table (status=confirmed). Surfaces all linked contacts,
// associated companies, and associated deals — same model as the
// Shopify Billing tab. Empty state explains the email-mismatch case.
// ============================================================

interface Props {
  email: string;
}

function fmtMoney(n: number | null): string {
  if (n == null) return "—";
  return `$${n.toLocaleString(undefined, {
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  })}`;
}

function fmtDate(s: string | null): string {
  if (!s) return "—";
  const d = DateTime.fromISO(s);
  return d.isValid ? d.toFormat("MMM d, yyyy") : "—";
}

function stageBadge(stage: string | null): React.ReactNode {
  if (!stage) return <span style={{ color: "#ababb7" }}>—</span>;
  const lower = stage.toLowerCase();
  let color = "#ababb7";
  if (/closedwon/.test(lower)) color = "#4CAF50";
  else if (/closedlost/.test(lower)) color = "#F44336";
  else if (
    /contractsent|qualifiedtobuy|appointmentscheduled|presentationscheduled|decisionmakerboughtin/.test(
      lower
    )
  )
    color = "#FFC107";
  return (
    <span
      style={{
        background: `${color}22`,
        color,
        padding: "2px 8px",
        borderRadius: 3,
        fontSize: 12,
        fontWeight: 500,
      }}
    >
      {stage}
    </span>
  );
}

function dealsList(deals: HubspotDeal[]): React.ReactNode {
  if (deals.length === 0) {
    return <p style={{ color: "#ababb7" }}>No deals.</p>;
  }
  return (
    <div style={{ overflowX: "auto" }}>
      <table style={{ width: "100%", borderCollapse: "collapse" }}>
        <thead>
          <tr style={{ borderBottom: "1px solid #424258" }}>
            <th style={{ textAlign: "left", padding: 8, fontSize: 12 }}>
              Deal
            </th>
            <th style={{ textAlign: "left", padding: 8, fontSize: 12 }}>
              Stage
            </th>
            <th style={{ textAlign: "right", padding: 8, fontSize: 12 }}>
              Amount
            </th>
            <th style={{ textAlign: "left", padding: 8, fontSize: 12 }}>
              Close Date
            </th>
            <th style={{ textAlign: "left", padding: 8, fontSize: 12 }}>
              Updated
            </th>
          </tr>
        </thead>
        <tbody>
          {deals.map((d) => (
            <tr key={d.hubspotId} style={{ borderBottom: "1px solid #2a2c3a" }}>
              <td style={{ padding: 8, fontSize: 13 }}>{d.name ?? "—"}</td>
              <td style={{ padding: 8 }}>{stageBadge(d.stage)}</td>
              <td
                style={{
                  padding: 8,
                  fontSize: 13,
                  textAlign: "right",
                  fontWeight: 500,
                }}
              >
                {fmtMoney(d.amount)}
              </td>
              <td style={{ padding: 8, fontSize: 13, color: "#ababb7" }}>
                {fmtDate(d.closeDate)}
              </td>
              <td style={{ padding: 8, fontSize: 13, color: "#ababb7" }}>
                {fmtDate(d.hubspotUpdatedAt)}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

const ENGAGEMENT_TYPE_LABEL: Record<string, string> = {
  note: "Note",
  call: "Call",
  meeting: "Meeting",
  email: "Email",
  task: "Task",
};

const ENGAGEMENT_TYPE_COLOR: Record<string, string> = {
  note: "#FFC107",
  call: "#00b2ff",
  meeting: "#9c27b0",
  email: "#4CAF50",
  task: "#ababb7",
};

function fmtDuration(ms: number | null): string {
  if (ms == null || !isFinite(ms) || ms <= 0) return "";
  const totalSec = Math.round(ms / 1000);
  const m = Math.floor(totalSec / 60);
  const s = totalSec % 60;
  if (m === 0) return `${s}s`;
  return `${m}m${s ? ` ${s}s` : ""}`;
}

function fmtRelativeAndDate(iso: string | null): string {
  if (!iso) return "—";
  const d = DateTime.fromISO(iso);
  if (!d.isValid) return "—";
  return `${d.toRelative()} (${d.toFormat("MMM d, yyyy")})`;
}

interface EngagementActivityProps {
  email: string;
}

const EngagementActivity: React.FC<EngagementActivityProps> = ({ email }) => {
  const [rows, setRows] = useState<HubspotEngagement[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [typeFilter, setTypeFilter] = useState<string>("");
  const PAGE_SIZE = 25;
  const [offset, setOffset] = useState(0);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    APIService.ADMIN_ANALYTICS.GET_CUSTOMER_HUBSPOT_ENGAGEMENTS(email, {
      limit: PAGE_SIZE,
      offset,
      type: typeFilter || undefined,
    })
      .then((data) => {
        if (cancelled) return;
        setRows(data.rows);
        setTotal(data.total);
      })
      .catch((e) => {
        console.error("Failed to load engagements", e);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [email, typeFilter, offset]);

  return (
    <div className={profileStyles.card}>
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          marginBottom: 8,
        }}
      >
        <h3 className={profileStyles.card__title} style={{ margin: 0 }}>
          HubSpot Activity ({total})
        </h3>
        <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
          {[
            { id: "", label: "All" },
            { id: "note", label: "Notes" },
            { id: "call", label: "Calls" },
            { id: "meeting", label: "Meetings" },
            { id: "email", label: "Emails" },
          ].map((t) => (
            <button
              key={t.id}
              onClick={() => {
                setTypeFilter(t.id);
                setOffset(0);
              }}
              style={{
                background:
                  typeFilter === t.id
                    ? "rgba(33, 150, 243, 0.18)"
                    : "transparent",
                border:
                  typeFilter === t.id
                    ? "1px solid #1565c0"
                    : "1px solid #424258",
                color: typeFilter === t.id ? "#fff" : "#ababb7",
                padding: "3px 10px",
                borderRadius: 4,
                fontSize: 11,
                fontWeight: 600,
                cursor: "pointer",
              }}
            >
              {t.label}
            </button>
          ))}
        </div>
      </div>
      {loading && (
        <div style={{ color: "#ababb7", fontSize: 13 }}>Loading activity…</div>
      )}
      {!loading && rows.length === 0 && (
        <div style={{ color: "#ababb7", fontSize: 13, padding: "8px 0" }}>
          No HubSpot engagement history yet for this contact. (Run the HubSpot
          bulk import if you expect activity here.)
        </div>
      )}
      {!loading && rows.length > 0 && (
        <>
          <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
            {rows.map((e) => {
              const color =
                ENGAGEMENT_TYPE_COLOR[e.engagementType] || "#ababb7";
              return (
                <div
                  key={`${e.hubspotId}-${e.engagementType}`}
                  style={{
                    display: "flex",
                    gap: 12,
                    padding: "12px 0",
                    borderBottom: "1px solid #424258",
                  }}
                >
                  <div
                    style={{
                      width: 8,
                      flexShrink: 0,
                      borderRadius: 2,
                      background: color,
                    }}
                  />
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div
                      style={{
                        display: "flex",
                        gap: 8,
                        alignItems: "baseline",
                        flexWrap: "wrap",
                      }}
                    >
                      <span
                        style={{
                          background: `${color}22`,
                          color,
                          padding: "1px 8px",
                          borderRadius: 3,
                          fontSize: 11,
                          fontWeight: 600,
                          textTransform: "uppercase",
                          letterSpacing: 0.4,
                        }}
                      >
                        {ENGAGEMENT_TYPE_LABEL[e.engagementType] ||
                          e.engagementType}
                      </span>
                      {e.direction && (
                        <span
                          style={{
                            color: "#ababb7",
                            fontSize: 11,
                            textTransform: "uppercase",
                          }}
                        >
                          {e.direction}
                        </span>
                      )}
                      {e.subject && (
                        <span style={{ fontSize: 13, fontWeight: 600 }}>
                          {e.subject}
                        </span>
                      )}
                      <span
                        style={{
                          color: "#ababb7",
                          fontSize: 12,
                          marginLeft: "auto",
                        }}
                      >
                        {fmtRelativeAndDate(e.occurredAt)}
                      </span>
                    </div>
                    {e.body && (
                      <div
                        style={{
                          fontSize: 13,
                          color: "#ddd",
                          marginTop: 6,
                          maxHeight: 90,
                          overflow: "hidden",
                          whiteSpace: "pre-wrap",
                          textOverflow: "ellipsis",
                        }}
                      >
                        {e.body.length > 500
                          ? `${e.body.slice(0, 500)}…`
                          : e.body}
                      </div>
                    )}
                    {(e.outcome || (e.durationMs && e.durationMs > 0)) && (
                      <div
                        style={{
                          fontSize: 11,
                          color: "#ababb7",
                          marginTop: 6,
                          display: "flex",
                          gap: 12,
                        }}
                      >
                        {e.outcome && <span>Outcome: {e.outcome}</span>}
                        {e.durationMs ? (
                          <span>Duration: {fmtDuration(e.durationMs)}</span>
                        ) : null}
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              marginTop: 12,
              fontSize: 12,
              color: "#ababb7",
            }}
          >
            <div>
              Showing {offset + 1}–{Math.min(offset + rows.length, total)} of{" "}
              {total}
            </div>
            <div style={{ display: "flex", gap: 8 }}>
              <button
                disabled={offset === 0}
                onClick={() => setOffset(Math.max(0, offset - PAGE_SIZE))}
              >
                ← Prev
              </button>
              <button
                disabled={offset + PAGE_SIZE >= total}
                onClick={() => setOffset(offset + PAGE_SIZE)}
              >
                Next →
              </button>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

const HubspotTab: React.FC<Props> = ({ email }) => {
  const [summary, setSummary] = useState<HubspotCustomerSummary | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    APIService.ADMIN_ANALYTICS.GET_CUSTOMER_HUBSPOT(email)
      .then((s) => {
        if (!cancelled) setSummary(s);
      })
      .catch((e) => {
        console.error("Failed to load HubSpot summary", e);
        if (!cancelled) setError("Could not load HubSpot data");
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [email]);

  if (loading) {
    return (
      <div className={profileStyles.tabContent}>
        <div className={profileStyles.card}>Loading HubSpot data…</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className={profileStyles.tabContent}>
        <div className={profileStyles.card}>
          <h3 className={profileStyles.card__title}>HubSpot</h3>
          <p style={{ color: "#F44336" }}>{error}</p>
        </div>
      </div>
    );
  }

  if (!summary || summary.contacts.length === 0) {
    return (
      <div className={profileStyles.tabContent}>
        <div className={profileStyles.card}>
          <h3 className={profileStyles.card__title}>HubSpot</h3>
          <p style={{ color: "#ababb7", marginTop: 8 }}>
            No HubSpot contact linked to <strong>{email}</strong>. The
            license-holder email may not match the contact email in HubSpot —
            check the Import &amp; Sync page to manually approve a suggested
            link if a fuzzy match exists.
          </p>
        </div>
      </div>
    );
  }

  const { contacts, companies, deals, stats } = summary;

  return (
    <div className={profileStyles.tabContent}>
      <div className={profileStyles.cardGrid}>
        {/* Stats */}
        <div className={profileStyles.card}>
          <h3 className={profileStyles.card__title}>HubSpot Summary</h3>
          <div className={profileStyles.statGrid}>
            <div className={profileStyles.stat}>
              <div className={profileStyles.stat__label}>Contacts</div>
              <div className={profileStyles.stat__value}>
                {stats.contactCount}
              </div>
            </div>
            <div className={profileStyles.stat}>
              <div className={profileStyles.stat__label}>Companies</div>
              <div className={profileStyles.stat__value}>
                {stats.companyCount}
              </div>
            </div>
            <div className={profileStyles.stat}>
              <div className={profileStyles.stat__label}>Deals</div>
              <div className={profileStyles.stat__value}>{stats.dealCount}</div>
            </div>
            <div className={profileStyles.stat}>
              <div className={profileStyles.stat__label}>Open pipeline</div>
              <div className={profileStyles.stat__value}>
                {fmtMoney(stats.openDealAmount)}
              </div>
            </div>
            <div className={profileStyles.stat}>
              <div className={profileStyles.stat__label}>Closed-won total</div>
              <div
                className={profileStyles.stat__value}
                style={{ color: "#4CAF50" }}
              >
                {fmtMoney(stats.closedWonAmount)}
              </div>
            </div>
          </div>
        </div>

        {/* Contact card (multiple if more than one — rare but possible) */}
        {contacts.map((c) => (
          <div key={c.hubspotId} className={profileStyles.card}>
            <h3 className={profileStyles.card__title}>
              Contact:{" "}
              {[c.firstName, c.lastName].filter(Boolean).join(" ") ||
                c.email ||
                "(no name)"}
            </h3>
            <div className={profileStyles.statGrid}>
              <div className={profileStyles.stat}>
                <div className={profileStyles.stat__label}>Email</div>
                <div className={profileStyles.stat__value}>
                  {c.email ?? "—"}
                </div>
              </div>
              <div className={profileStyles.stat}>
                <div className={profileStyles.stat__label}>Phone</div>
                <div className={profileStyles.stat__value}>
                  {c.phone ?? "—"}
                </div>
              </div>
              <div className={profileStyles.stat}>
                <div className={profileStyles.stat__label}>Job Title</div>
                <div className={profileStyles.stat__value}>
                  {c.jobTitle ?? "—"}
                </div>
              </div>
              <div className={profileStyles.stat}>
                <div className={profileStyles.stat__label}>Company (free)</div>
                <div className={profileStyles.stat__value}>
                  {c.companyName ?? "—"}
                </div>
              </div>
              <div className={profileStyles.stat}>
                <div className={profileStyles.stat__label}>City</div>
                <div className={profileStyles.stat__value}>{c.city ?? "—"}</div>
              </div>
              <div className={profileStyles.stat}>
                <div className={profileStyles.stat__label}>State</div>
                <div className={profileStyles.stat__value}>
                  {c.state ?? "—"}
                </div>
              </div>
              <div className={profileStyles.stat}>
                <div className={profileStyles.stat__label}>Lifecycle</div>
                <div className={profileStyles.stat__value}>
                  {c.lifecycleStage ?? "—"}
                </div>
              </div>
              <div className={profileStyles.stat}>
                <div className={profileStyles.stat__label}>Updated</div>
                <div className={profileStyles.stat__value}>
                  {fmtDate(c.hubspotUpdatedAt)}
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Companies */}
      {companies.length > 0 && (
        <div className={profileStyles.card}>
          <h3 className={profileStyles.card__title}>
            Associated Companies ({companies.length})
          </h3>
          <div style={{ overflowX: "auto" }}>
            <table style={{ width: "100%", borderCollapse: "collapse" }}>
              <thead>
                <tr style={{ borderBottom: "1px solid #424258" }}>
                  <th style={{ textAlign: "left", padding: 8, fontSize: 12 }}>
                    Name
                  </th>
                  <th style={{ textAlign: "left", padding: 8, fontSize: 12 }}>
                    Domain
                  </th>
                  <th style={{ textAlign: "left", padding: 8, fontSize: 12 }}>
                    Industry
                  </th>
                  <th style={{ textAlign: "left", padding: 8, fontSize: 12 }}>
                    Location
                  </th>
                </tr>
              </thead>
              <tbody>
                {companies.map((c) => (
                  <tr
                    key={c.hubspotId}
                    style={{ borderBottom: "1px solid #2a2c3a" }}
                  >
                    <td style={{ padding: 8, fontSize: 13 }}>
                      {c.name ?? "—"}
                    </td>
                    <td style={{ padding: 8, fontSize: 13, color: "#ababb7" }}>
                      {c.domain ?? "—"}
                    </td>
                    <td style={{ padding: 8, fontSize: 13, color: "#ababb7" }}>
                      {c.industry ?? "—"}
                    </td>
                    <td style={{ padding: 8, fontSize: 13, color: "#ababb7" }}>
                      {[c.city, c.state].filter(Boolean).join(", ") || "—"}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Deals */}
      <div className={profileStyles.card}>
        <h3 className={profileStyles.card__title}>Deals ({deals.length})</h3>
        {dealsList(deals)}
      </div>

      {/* HubSpot Engagement Activity (notes/calls/meetings/emails) */}
      <EngagementActivity email={email} />
    </div>
  );
};

export default HubspotTab;
